import { useState } from 'react'
import { Check, Minus, Pause, Plus, X } from 'lucide-react'
import type { PauseAdr, PauseReview } from '../lib/pipeline'
import { parseAdrOptions } from '../lib/pipeline'
import { FEASIBILITY_LABELS } from '../lib/labels'

interface DecisionDrawerProps {
  open: boolean
  adr: PauseAdr | null
  reviews: PauseReview[]
  onClose: () => void
  onApprove: (feedback: string, optionName?: string) => void
  onReject: (feedback: string) => void
}

const SCORE: Record<string, number> = {
  viavel: 8,
  parcialmente_viavel: 6,
  inviavel: 3,
}

export default function DecisionDrawer({ open, adr, reviews, onClose, onApprove, onReject }: DecisionDrawerProps) {
  const [note, setNote] = useState('')
  const options = parseAdrOptions(adr?.block)
  const [opt, setOpt] = useState(0)

  if (!open || !adr) return null

  const chosen = options[opt]
  const approveLabel = chosen ? `Aprovar ${chosen.name}` : 'Aprovar recomendação'

  return (
    <div className="absolute inset-0 z-40 bg-black/40 flex justify-end" onClick={onClose}>
      <div
        role="dialog"
        aria-labelledby="decision-title"
        className="w-[620px] max-w-full h-full bg-surface border-l border-line flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2.5 px-4 py-3.5 border-b border-line">
          <span className="font-mono text-[11px] font-semibold text-warn bg-warn-soft px-1.5 py-0.5 rounded">
            {adr.adr_id || 'ADR'}
          </span>
          <span id="decision-title" className="font-semibold text-[15px] flex-1 min-w-0 truncate">
            {adr.adr_title || 'Decisão pendente'}
          </span>
          <button type="button" onClick={onClose} className="w-7 h-7 rounded-md text-muted hover:bg-surface-2 cursor-pointer" aria-label="Fechar">
            <X size={15} className="mx-auto" />
          </button>
        </div>

        <div className="flex-1 overflow-auto px-4 py-4 flex flex-col gap-4">
          <div className="flex items-start gap-2.5 p-2.5 rounded-lg bg-warn-soft border border-warn-line text-[12.5px] leading-relaxed">
            <Pause size={14} className="text-warn mt-0.5 shrink-0" />
            <p>
              <b>Archie pausou a síntese.</b> Jobs e Van Gogh esperam esta decisão para gerar Event Storm, diagramas e protótipo.
            </p>
          </div>

          {adr.block && (
            <div className="flex flex-col gap-1.5">
              <span className="font-mono text-[10.5px] uppercase tracking-wide text-faint">Contexto do ADR</span>
              <pre className="whitespace-pre-wrap font-sans text-[12.5px] leading-relaxed text-muted m-0">{adr.block.slice(0, 1200)}</pre>
            </div>
          )}

          {reviews.length > 0 && (
            <div className="flex flex-col gap-2">
              <span className="font-mono text-[10.5px] uppercase tracking-wide text-faint">Notas do Dochie</span>
              <div className="grid grid-cols-2 gap-2">
                {reviews.map((review, i) => {
                  const score = SCORE[review.feasibility || ''] ?? 5
                  return (
                    <div key={i} className="border border-line rounded-lg p-2.5 flex flex-col gap-1.5">
                      <div className="flex justify-between text-[12px]">
                        <span className="font-medium capitalize">{review.perspective || 'Revisão'}</span>
                        <span className="font-mono text-muted">{FEASIBILITY_LABELS[review.feasibility || ''] || review.feasibility}</span>
                      </div>
                      <div className="h-1 rounded bg-line">
                        <div className="h-1 rounded bg-ink" style={{ width: `${score * 10}%` }} />
                      </div>
                      {review.risks?.[0] && <p className="text-[11.5px] text-muted leading-snug m-0">{review.risks[0]}</p>}
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          {options.length > 0 && (
            <div className="flex flex-col gap-2">
              <span className="font-mono text-[10.5px] uppercase tracking-wide text-faint">Opções</span>
              {options.map((option, i) => {
                const on = opt === i
                return (
                  <button
                    key={option.name}
                    type="button"
                    onClick={() => setOpt(i)}
                    className={`text-left rounded-[10px] border-[1.5px] px-3.5 py-3 cursor-pointer ${on ? 'border-ink bg-surface-2' : 'border-line bg-surface'}`}
                  >
                    <div className="flex items-center gap-2">
                      <span className={`w-4 h-4 rounded-full border flex items-center justify-center ${on ? 'border-ink' : 'border-faint'}`}>
                        {on && <span className="w-2 h-2 rounded-full bg-ink" />}
                      </span>
                      <span className="font-semibold text-[13.5px]">{option.name}</span>
                    </div>
                    {option.detail && (
                      <div className="mt-2 grid grid-cols-2 gap-2 text-[12px] text-muted">
                        <span className="flex gap-1.5"><Plus size={12} className="text-ok mt-0.5 shrink-0" />{option.detail.split('—')[0]}</span>
                        {option.detail.includes('—') && (
                          <span className="flex gap-1.5"><Minus size={12} className="text-bad mt-0.5 shrink-0" />{option.detail.split('—').slice(1).join('—')}</span>
                        )}
                      </div>
                    )}
                  </button>
                )
              })}
            </div>
          )}

          <label className="flex flex-col gap-1.5">
            <span className="font-mono text-[10.5px] uppercase tracking-wide text-faint">Orientação para o Archie (opcional)</span>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              placeholder="ex.: aprovar a opção gerenciada e revisar custo em 6 meses"
              className="border border-line rounded-lg px-2.5 py-2 bg-surface-2 text-[12.5px] text-ink outline-none resize-y"
            />
          </label>
        </div>

        <div className="flex items-center gap-2 px-4 py-3 border-t border-line bg-surface-2">
          <button
            type="button"
            onClick={() => onReject(note.trim())}
            className="h-9 px-3 rounded-lg border border-line bg-surface text-[12.5px] cursor-pointer"
          >
            Rejeitar e pedir revisão
          </button>
          <span className="flex-1 text-[11.5px] text-faint text-right">A decisão fica registrada no ADR</span>
          <button
            type="button"
            onClick={() => onApprove(note.trim(), chosen?.name)}
            className="h-9 inline-flex items-center gap-1.5 px-3.5 rounded-lg bg-ink text-surface text-[12.5px] font-semibold cursor-pointer"
          >
            <Check size={14} />
            {approveLabel}
          </button>
        </div>
      </div>
    </div>
  )
}
